// Side-jobs strip helpers (Mark, 3 Nov 2026: "I think we need some sort of evidence
// within a given session if there are sub agents running"). Pure so they can be
// tested without React. The strip only reads /api/chats/{id}/side-jobs; it never
// sends anything to the lane, the provider or the side topic.

export type SideJobState =
  | 'queued' | 'running' | 'dispatched_unconfirmed' | 'retry_wait' | 'failed' | 'stopped' | 'done'

export interface SideJob {
  topic_id: string
  chat_id: string
  title: string
  state: SideJobState
  note?: string | null
  retries?: number | null
  next_retry_at?: number | null
  url: string
  created_at: number
  last_activity_at: number
}

export interface SideJobsResponse {
  jobs: SideJob[]
  active: number
  now: number
}

export const SIDE_JOBS_POLL_MS = 30_000

export const STATE_LABEL: Record<SideJobState, string> = {
  queued: 'Queued',
  running: 'Running',
  dispatched_unconfirmed: 'Not confirmed',
  retry_wait: 'Retrying',
  failed: 'Failed',
  stopped: 'Stopped',
  done: 'Done',
}

export function sideJobsPath(chatId: string, topicId: string | null): string {
  const base = `/api/chats/${encodeURIComponent(chatId)}/side-jobs`
  return topicId ? `${base}?topic_id=${encodeURIComponent(topicId)}` : base
}

export function sideJobPath(job: Pick<SideJob, 'chat_id' | 'topic_id'>): string {
  return `/chats/${encodeURIComponent(job.chat_id)}?topic_id=${encodeURIComponent(job.topic_id)}`
}

/** "1 running · 2 done": counts by state, active states first, zero counts omitted. */
export function stripSummary(jobs: SideJob[]): string {
  const order: SideJobState[] = ['running', 'queued', 'retry_wait', 'dispatched_unconfirmed', 'failed', 'stopped', 'done']
  return order
    .map(state => [state, jobs.filter(job => job.state === state).length] as const)
    .filter(([, count]) => count > 0)
    .map(([state, count]) => `${count} ${STATE_LABEL[state].toLowerCase()}`)
    .join(' · ')
}

function minutes(seconds: number): string {
  const mins = Math.max(0, Math.round(seconds / 60))
  return mins < 60 ? `${mins} min` : `${(mins / 60).toFixed(1)} h`
}

export function formatClock(epochSeconds: number, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-IE', {
    hour: '2-digit', minute: '2-digit', hour12: false, ...(timeZone ? { timeZone } : {}),
  }).format(new Date(epochSeconds * 1000))
}

/** When or how long, in the words a phone row has room for. */
export function jobTiming(job: SideJob, nowSeconds: number, timeZone?: string): string {
  switch (job.state) {
    case 'running':
    case 'dispatched_unconfirmed':
      return `started ${minutes(nowSeconds - job.created_at)} ago`
    case 'queued':
      return `waiting ${minutes(nowSeconds - job.created_at)}`
    case 'retry_wait':
      return job.next_retry_at ? `next try ${formatClock(job.next_retry_at, timeZone)}` : 'will retry'
    default:
      return `${STATE_LABEL[job.state].toLowerCase()} ${formatClock(job.last_activity_at, timeZone)}`
  }
}

export function isActive(job: SideJob): boolean {
  return ['queued', 'running', 'dispatched_unconfirmed', 'retry_wait', 'failed'].includes(job.state)
}
